import { useCallback } from 'react';

import { useTokenClaims } from './use-token-claims';

import type { PermissionString } from '@/types/permissions';

interface UsePermissionsReturn {
  permissions: PermissionString[];
  hasPermission: (...required: PermissionString[]) => boolean;
  hasAnyPermission: (...required: PermissionString[]) => boolean;
  loading: boolean;
}

const NONE: PermissionString[] = [];

/** The viewer's Auth0 grants, read off the shared access-token decode. Reading
 *  the same token as `useAppMode` is the point: chrome derived from one token's
 *  roles and gated on another token's permissions can disagree. */
export function usePermissions(): UsePermissionsReturn {
  const { claims, loading } = useTokenClaims();
  const raw = claims?.permissions;
  const permissions = Array.isArray(raw) ? (raw as PermissionString[]) : NONE;

  const hasPermission = useCallback(
    (...required: PermissionString[]) => required.every((p) => permissions.includes(p)),
    [permissions],
  );

  const hasAnyPermission = useCallback(
    (...required: PermissionString[]) => required.some((p) => permissions.includes(p)),
    [permissions],
  );

  return { permissions, hasPermission, hasAnyPermission, loading };
}
